import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { 
  Building2, 
  Users, 
  User, 
  GraduationCap, 
  Crown, 
  Zap, 
  Rocket, 
  Globe,
  ArrowRight 
} from "lucide-react";
import { Link } from "react-router-dom";

const solutions = [
  {
    icon: Building2,
    title: "Digital Agencies",
    description: "Scale campaign creation with enforced budget ceilings so clients never complain about wasted spend.",
    link: "/agency",
    bgColor: "bg-blue-50",
    iconBg: "bg-white"
  },
  {
    icon: Users,
    title: "Freelancers",
    description: "Deliver agency-quality work while automatically protecting your clients' budgets from expensive clicks.",
    link: "/freelancer",
    bgColor: "bg-background",
    iconBg: "bg-blue-50"
  },
  {
    icon: User,
    title: "Solopreneurs",
    description: "Bootstrap your business with strict CPA guardrails. Every dollar is tracked against profitability.",
    link: "/solopreneur",
    bgColor: "bg-background",
    iconBg: "bg-blue-50"
  },
  {
    icon: GraduationCap,
    title: "Beginners",
    description: "Learn Google Ads safely. Our AI prevents you from making expensive budget-draining mistakes.",
    link: "/beginner",
    bgColor: "bg-background",
    iconBg: "bg-blue-50"
  },
  {
    icon: Crown,
    title: "CEOs & Business Owners",
    description: "Stop burning cash on vague brand awareness. Target exact intent and protect your bottom line.",
    link: "/business-owners",
    bgColor: "bg-background",
    iconBg: "bg-blue-50"
  },
  {
    icon: Zap,
    title: "SaaS Companies",
    description: "Protect your runway from $80 B2B clicks. Filter out budget-draining keywords automatically.",
    link: "/saas",
    bgColor: "bg-background",
    iconBg: "bg-blue-50"
  },
  {
    icon: Rocket,
    title: "Startups",
    description: "Launch fast with limited runway. Get maximum impact by ruthlessly filtering out non-converting clicks.",
    link: "/startup",
    bgColor: "bg-background",
    iconBg: "bg-blue-50"
  },
  {
    icon: Globe,
    title: "International Businesses",
    description: "Expand globally without blowing your budget on unpredictable international click costs.",
    link: "/international",
    bgColor: "bg-background",
    iconBg: "bg-blue-50"
  }
];

const SolutionsSection = () => {
  return (
    <section className="py-24 px-4 bg-gradient-to-br from-white to-blue-50">
      <div className="container mx-auto max-w-7xl">
        <div className="text-center mb-16">
          <Badge className="px-3 py-1 text-sm bg-blue-600 text-white border-blue-700 mb-4">
            Tailored Solutions
          </Badge>
          <h2 className="text-3xl md:text-5xl font-bold mb-6">
            Built For Your Success. Designed For Your Goals.
          </h2>
          <p className="text-xl text-muted-foreground max-w-4xl mx-auto">
            Whether you're scaling an agency, starting your first campaign, or growing a global business - we have the perfect solution for you.
          </p>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {solutions.map((solution, index) => {
            const IconComponent = solution.icon;
            return (
              <Card 
                key={index} 
                className={`${solution.bgColor} border border-blue-500/20 hover:shadow-lg transition-all duration-300 overflow-hidden group hover:border-blue-500/40`}
              >
                <CardContent className="p-6 space-y-4 h-full flex flex-col">
                  <div className={`${solution.iconBg} rounded-full w-12 h-12 flex items-center justify-center shadow-sm group-hover:scale-110 transition-transform duration-300`}>
                    <IconComponent size={24} className="text-blue-600" />
                  </div>
                  <h3 className="text-lg font-bold">{solution.title}</h3>
                  <p className="text-muted-foreground text-sm leading-relaxed flex-grow">
                    {solution.description}
                  </p>
                  <Button variant="outline" size="sm" className="w-full mt-auto" asChild>
                    <Link to={solution.link}>
                      Learn More <ArrowRight className="ml-2 h-3 w-3" />
                    </Link>
                  </Button>
                </CardContent>
              </Card>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default SolutionsSection;